import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, ChevronDown, Menu, X, Search, User, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useWishlist } from '../context/WishlistContext';
import { formatFCFA } from '../lib/currency';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { label: 'Soin de visage', value: 'Soin de visage', subs: [
    { label: 'Taches & hyperpigmentation', value: 'Taches & hyperpigmentation' },
    { label: 'Acn\u00e9 & imperfections', value: 'Acn\u00e9 & imperfections' },
    { label: 'Peaux s\u00e8ches & d\u00e9shydrat\u00e9es', value: 'Peaux s\u00e8ches & d\u00e9shydrat\u00e9es' },
    { label: 'Peaux sensibles', value: 'Peaux sensibles' },
  ]},
  { label: 'Soin de corps', value: 'Soin de corps', subs: [
    { label: 'Savons & nettoyants', value: 'Savons & nettoyants' },
    { label: 'Gommages & exfoliants', value: 'Gommages & exfoliants' },
    { label: 'Gels douche', value: 'Gels douche' },
    { label: 'Mousses nettoyantes', value: 'Mousses nettoyantes' },
    { label: 'Brumes parfum\u00e9es', value: 'Brumes parfum\u00e9es' },
    { label: 'Lait de Corps', value: 'Lait de Corps' },
  ]},
  { label: 'Compl\u00e9ment alimentaire', value: 'Compl\u00e9ment alimentaire' },
];

function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  const searchProducts = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products`, { params: { q: q.trim() } });
      setResults(res.data || []);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(query), 250);
    return () => clearTimeout(timer);
  }, [query, searchProducts]);

  const handleSelect = (productId) => {
    onClose();
    navigate(`/product/${productId}`);
  };

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80]" data-testid="search-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />

      {/* Search Panel */}
      <div className="relative bg-[#F5F0E8] w-full shadow-2xl animate-in" style={{ animation: 'slideDown 0.3s ease-out' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Input */}
          <div className="flex items-center gap-4 border-b-2 border-black pb-3">
            <Search size={20} strokeWidth={1.5} className="text-black/40 flex-shrink-0" />
            <input
              ref={inputRef}
              data-testid="search-input"
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Rechercher un produit..."
              className="flex-1 bg-transparent outline-none text-lg font-light placeholder:text-black/30"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            />
            <button data-testid="search-close-btn" onClick={onClose} className="p-1 hover:opacity-50 transition-opacity">
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* Results */}
          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {loading && (
              <p className="text-sm text-black/30 py-4 text-center">Recherche en cours...</p>
            )}

            {!loading && query.trim() && results.length === 0 && (
              <p className="text-sm text-black/40 py-8 text-center">Aucun r&eacute;sultat pour &laquo; {query} &raquo;</p>
            )}

            {!loading && results.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/30 mb-3">
                  {results.length} r&eacute;sultat{results.length > 1 ? 's' : ''}
                </p>
                {results.map(product => (
                  <button
                    key={product.product_id}
                    data-testid={`search-result-${product.product_id}`}
                    onClick={() => handleSelect(product.product_id)}
                    className="w-full flex items-center gap-4 p-3 hover:bg-[#EDE8DE] transition-colors text-left group"
                  >
                    <div className="w-14 h-14 bg-[#EDE8DE] flex-shrink-0 overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        style={{ mixBlendMode: 'multiply' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-base font-normal text-black truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {product.name}
                      </h4>
                      <p className="text-xs text-black/40 mt-0.5">
                        {product.product_type} &middot; {formatFCFA(product.price)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!query.trim() && (
              <div className="py-8 text-center">
                <p className="text-sm text-black/30">Tapez pour rechercher des produits, cat&eacute;gories ou types de peau</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default function Navbar() {
  const { totalItems, setIsOpen } = useCart();
  const { customer } = useCustomerAuth();
  const { count: wishlistCount } = useWishlist();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const menuRef = useRef(null);

  if (location.pathname.startsWith('/dashboard')) return null;

  const handleCategoryClick = (value) => {
    setMenuOpen(false);
    setMobileOpen(false);
    setExpandedCat(null);
    navigate(`/shop?type=${encodeURIComponent(value)}`);
  };

  const handleCatToggle = (cat) => {
    if (cat.subs) {
      setExpandedCat(expandedCat === cat.value ? null : cat.value);
    } else {
      handleCategoryClick(cat.value);
    }
  };

  return (
    <>
      <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 bg-[#F5F0E8]/90 backdrop-blur-md border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo — transparent PNG (text blends into the beige navbar) */}
            <Link to="/" data-testid="nav-logo" className="flex items-center -ml-2 sm:-ml-3 lg:-ml-5" aria-label="Balmart">
              <img
                src="/logo/balmart-logo-horizontal-transparent.png"
                alt="Balmart · Spend Less"
                className="h-10 sm:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Nav — 4 liens simples, texte noir plein */}
            <div className="hidden md:flex items-center gap-10">
              <Link to="/" data-testid="nav-accueil-link" className="text-xs uppercase tracking-[0.2em] font-semibold text-black hover:text-[#B80000] transition-colors">
                Accueil
              </Link>
              <Link to="/shop" data-testid="nav-shop-link" className="text-xs uppercase tracking-[0.2em] font-semibold text-black hover:text-[#B80000] transition-colors">
                Boutique
              </Link>
              <Link to="/routine" data-testid="nav-routine-link" className="text-xs uppercase tracking-[0.2em] font-semibold text-black hover:text-[#B80000] transition-colors">
                La Routine
              </Link>
              <Link to="/about" data-testid="nav-about-link" className="text-xs uppercase tracking-[0.2em] font-semibold text-black hover:text-[#B80000] transition-colors">
                &Agrave; propos de nous
              </Link>
            </div>

            {/* Right: User + Search + Cart + Mobile toggle */}
            <div className="flex items-center gap-1 sm:gap-3">
              <Link
                to={customer ? "/account" : "/login"}
                data-testid="user-btn"
                className="p-2 hover:opacity-60 transition-opacity"
              >
                <User size={20} strokeWidth={1.5} />
              </Link>

              <Link
                to={customer ? "/wishlist" : "/login"}
                data-testid="wishlist-btn"
                className="hidden xs:inline-flex relative p-2 hover:opacity-60 transition-opacity"
                aria-label="Favoris"
              >
                <Heart size={20} strokeWidth={1.5} />
                {customer && wishlistCount > 0 && (
                  <span data-testid="wishlist-count" className="absolute -top-1 -right-1 w-5 h-5 text-white text-[10px] flex items-center justify-center" style={{ backgroundColor: '#B80000' }}>
                    {wishlistCount}
                  </span>
                )}
              </Link>

              <button
                data-testid="search-btn"
                onClick={() => setSearchOpen(true)}
                className="p-2 hover:opacity-60 transition-opacity"
              >
                <Search size={20} strokeWidth={1.5} />
              </button>

              <button
                data-testid="cart-button"
                onClick={() => setIsOpen(true)}
                className="relative p-2 hover:opacity-60 transition-opacity"
              >
                <ShoppingBag size={20} strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span data-testid="cart-count" className="absolute -top-1 -right-1 w-5 h-5 text-white text-[10px] flex items-center justify-center" style={{ backgroundColor: '#B80000' }}>
                    {totalItems}
                  </span>
                )}
              </button>

              <button
                data-testid="mobile-menu-btn"
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden p-2 hover:opacity-60 transition-opacity"
              >
                {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Search Overlay */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-40 bg-[#F5F0E8] pt-20 overflow-y-auto">
          <div className="px-6 py-8 space-y-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block px-2 py-4 text-sm uppercase tracking-[0.25em] font-bold text-black border-b border-black/10">
              Accueil
            </Link>

            {/* Boutique with expandable categories */}
            <div className="border-b border-black/10">
              <button
                data-testid="mobile-boutique-btn"
                onClick={() => setExpandedCat(expandedCat === '__shop' ? null : '__shop')}
                className="flex items-center justify-between w-full text-left px-2 py-4 text-sm uppercase tracking-[0.25em] font-bold text-black"
              >
                Boutique
                <ChevronDown size={14} className={`transition-transform duration-300 ${expandedCat === '__shop' ? 'rotate-180' : ''}`} />
              </button>
              {expandedCat === '__shop' && (
                <div className="pb-3 pl-2">
                  <Link to="/shop" onClick={() => setMobileOpen(false)} className="block px-2 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold text-black hover:text-[#B80000]">
                    Voir tout
                  </Link>
                  {CATEGORIES.map(cat => (
                    <div key={cat.value}>
                      <button
                        data-testid={`mobile-category-${cat.value.replace(/\s/g, '-').toLowerCase()}`}
                        onClick={() => {
                          if (cat.subs) {
                            setExpandedCat(expandedCat === cat.value ? '__shop' : cat.value);
                          } else {
                            handleCategoryClick(cat.value);
                          }
                        }}
                        className="flex items-center justify-between w-full text-left px-2 py-2.5 text-xs uppercase tracking-[0.15em] font-medium text-black/80 hover:text-black"
                      >
                        {cat.label}
                        {cat.subs && <ChevronDown size={12} className={`transition-transform duration-300 ${expandedCat === cat.value ? 'rotate-180' : ''}`} />}
                      </button>
                      {cat.subs && expandedCat === cat.value && (
                        <div className="pl-4 pb-2">
                          {cat.subs.map(sub => (
                            <button
                              key={sub.value}
                              onClick={() => { setMobileOpen(false); setExpandedCat(null); navigate(`/shop?type=${encodeURIComponent(cat.value)}&concern=${encodeURIComponent(sub.value)}`); }}
                              className="block w-full text-left px-2 py-2 text-xs text-black/60 hover:text-black"
                            >
                              → {sub.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link to="/routine" onClick={() => setMobileOpen(false)} className="block px-2 py-4 text-sm uppercase tracking-[0.25em] font-bold text-black border-b border-black/10">
              La Routine
            </Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="block px-2 py-4 text-sm uppercase tracking-[0.25em] font-bold text-black border-b border-black/10">
              &Agrave; propos de nous
            </Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block px-2 py-4 text-sm uppercase tracking-[0.25em] font-bold text-black border-b border-black/10">
              Contact
            </Link>

            {/* Utilities group (bottom) */}
            <div className="pt-6 space-y-1">
              <Link to={customer ? "/wishlist" : "/login"} onClick={() => setMobileOpen(false)} data-testid="mobile-wishlist-link" className="flex items-center gap-2 px-2 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-black/70 hover:text-black xs:hidden">
                <Heart size={14} strokeWidth={1.5} /> Favoris
                {customer && wishlistCount > 0 && (
                  <span className="text-[10px] text-white px-1.5 py-0.5" style={{ backgroundColor: '#B80000' }}>{wishlistCount}</span>
                )}
              </Link>
              <Link to={customer ? "/account" : "/login"} onClick={() => setMobileOpen(false)} className="block px-2 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-black/70 hover:text-black">
                {customer ? "Mon compte" : "Connexion"}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
